import axios from "helpers/api";
import { scheduleTokenOf } from "./scheduleTokens";

/**
 * Periodic audit — every call to the audit endpoints, and the normalisers that
 * turn their responses into what the screens use.
 *
 * PINNED by inventory.md (Rev 3) and used as-is:
 *   GET  /inventory/audit/state/:patientId       state, always_skippable
 *   GET  /inventory/audit/checklist/:patientId   expected_qty, no_balance_row (paginated)
 *   POST /inventory/audit/image
 *   POST /inventory/audit/submit/:patientId
 *   GET  /inventory/audit/history/:patientId     presigned image URLs, may be null
 *   GET  /inventory/audit/overdue
 *   Group config: inventory_audit_grace_days (-999 sentinel),
 *                 inventory_audit_blocks_medpass, audit_can_block
 *
 * NOT in the doc: every other field / param name. Each one is read through
 * `pick()` from a short list of likely names and marked  UNCONFIRMED .
 * When the real contract is known, this is the only file that needs editing.
 */

const authHeaders = () => {
  const tokenStr = localStorage.getItem("accessToken");
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl ? `${baseUrl}${path}` : path;
};

const hasValue = (v) => v !== undefined && v !== null && v !== "";

const pick = (obj, keys, fallback = null) => {
  for (const k of keys) {
    if (obj && hasValue(obj[k])) return obj[k];
  }
  return fallback;
};

const pickKey = (obj, keys) => keys.find((k) => obj && Object.prototype.hasOwnProperty.call(obj, k));

const toNumber = (v, fallback = null) =>
  hasValue(v) && Number.isFinite(Number(v)) ? Number(v) : fallback;

const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";

const asArray = (v) => (Array.isArray(v) ? v : []);

export const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

/* ------------------------------------------------------------------ */
/* Audit state                                                         */
/*                                                                     */
/* Same four states as the delivery gate. GRACE is the one that gets   */
/* forgotten — it is handled everywhere below. An unknown value is      */
/* NOT_REQUIRED, never blocking. The audit is a prompt, not a gate      */
/* (U15): nothing here ever stops anyone doing anything.               */
/* ------------------------------------------------------------------ */

export const AUDIT_STATE = {
  NOT_REQUIRED: "NOT_REQUIRED",
  GRACE: "GRACE",
  REQUIRED: "REQUIRED",
  CONFIRMED: "CONFIRMED"
};

// UNCONFIRMED aliases, in case the audit endpoint words its states differently.
const STATE_ALIASES = {
  COMPLETE: AUDIT_STATE.CONFIRMED,
  COMPLETED: AUDIT_STATE.CONFIRMED,
  DONE: AUDIT_STATE.CONFIRMED,
  DUE: AUDIT_STATE.GRACE,
  OVERDUE: AUDIT_STATE.REQUIRED,
  NOT_DUE: AUDIT_STATE.NOT_REQUIRED,
  NOT_CONFIGURED: AUDIT_STATE.NOT_REQUIRED,
  DISABLED: AUDIT_STATE.NOT_REQUIRED
};

export const normalizeAuditState = (state) => {
  const s = String(state || "").toUpperCase();
  return AUDIT_STATE[s] || STATE_ALIASES[s] || AUDIT_STATE.NOT_REQUIRED;
};

export const getAuditState = async (patientId) => {
  const res = await axios.get(apiUrl(`/inventory/audit/state/${patientId}`), {
    headers: authHeaders()
  });
  const d = res.data?.data || {};
  return {
    state: normalizeAuditState(d.state),
    rawState: d.state ?? null,
    // Read, never recomputed (U16). True when grace is -999 — the shipping default.
    alwaysSkippable: toBool(d.always_skippable),
    dueDate: pick(d, ["due_date", "next_due_date", "audit_due_date", "due_on"]), // UNCONFIRMED
    lastAuditAt: pick(d, ["last_audit_at", "last_audited_at", "last_audit_date", "last_completed_at"]), // UNCONFIRMED
    intervalDays: toNumber(pick(d, ["interval_days", "inventory_audit_interval_days"])), // UNCONFIRMED
    reason: pick(d, ["reason"]), // UNCONFIRMED
    raw: d
  };
};

/* ------------------------------------------------------------------ */
/* Checklist — the tracked meds to count                               */
/*                                                                     */
/* Paginated server-side. Every page is fetched up front so the user   */
/* can move between pages without losing counts, and submit covers     */
/* every line.                                                         */
/* ------------------------------------------------------------------ */

const CHECKLIST_PAGE_LIMIT = 100;
const CHECKLIST_MAX_PAGES = 30; // safety stop

const normalizeChecklistLine = (r, index) => {
  const prescriptionId = pick(r, ["prescription_id"]);
  const orderId = pick(r, ["pharmacy_order_id"]);
  return {
    key: `aud-${prescriptionId ?? orderId ?? "x"}-${index}`,
    prescription_id: prescriptionId,
    pharmacy_order_id: hasValue(orderId) ? String(orderId) : "",
    rx_number: pick(r, ["external_prescription_id", "rx_number"]), // UNCONFIRMED
    drug_name: pick(r, ["drug", "attestation_drug", "drug_name", "medication"], "—"), // UNCONFIRMED
    strength: pick(r, ["strength", "strength_value"], ""), // UNCONFIRMED
    schedule: scheduleTokenOf(pick(r, ["drug_schedule", "schedule"]), r.drug_subtype), // UNCONFIRMED
    // Pinned. With no_balance_row true, expected_qty 0 means "no stock
    // recorded" — NOT "we counted zero" (U16).
    expected_qty: toNumber(r.expected_qty, 0),
    no_balance_row: toBool(r.no_balance_row),
    raw: r
  };
};

export const getAuditChecklist = async (patientId) => {
  const rows = [];
  let total = null;
  let firstMeta = null;

  for (let page = 1; page <= CHECKLIST_MAX_PAGES; page += 1) {
    const res = await axios.get(apiUrl(`/inventory/audit/checklist/${patientId}`), {
      headers: authHeaders(),
      params: { page, limit: CHECKLIST_PAGE_LIMIT } // UNCONFIRMED param names
    });
    const body = res.data || {};
    const pageRows = Array.isArray(body.data)
      ? body.data
      : asArray(body.data?.lines || body.data?.items); // UNCONFIRMED nesting
    const meta = body.meta || body.pagination || {};
    if (!firstMeta) firstMeta = meta;
    if (total === null) total = toNumber(pick(meta, ["total", "total_count", "count"])); // UNCONFIRMED

    rows.push(...pageRows);

    if (pageRows.length < CHECKLIST_PAGE_LIMIT) break;
    if (total !== null && rows.length >= total) break;
  }

  return { lines: rows.map(normalizeChecklistLine), meta: firstMeta || {} };
};

/* ------------------------------------------------------------------ */
/* Image upload — required before an audit can be submitted            */
/* ------------------------------------------------------------------ */

export const MAX_AUDIT_IMAGE_BYTES = 10 * 1024 * 1024;

export const uploadAuditImage = async (file, patientId) => {
  const form = new FormData();
  form.append("image", file); // UNCONFIRMED field name
  if (hasValue(patientId)) form.append("patient_id", patientId); // UNCONFIRMED

  const res = await axios.post(apiUrl("/inventory/audit/image"), form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" }
  });
  const d = res.data?.data || res.data || {};
  return {
    imageId: pick(d, ["image_id", "id", "key", "image_key", "s3_key"]), // UNCONFIRMED
    url: pick(d, ["url", "image_url", "presigned_url"]), // UNCONFIRMED
    raw: d
  };
};

/* ------------------------------------------------------------------ */
/* Submit                                                              */
/* ------------------------------------------------------------------ */

export const buildAuditPayload = ({ imageId, lines }) => ({
  image_id: imageId, // UNCONFIRMED
  lines: lines.map((l) => {
    const line = {
      prescription_id: l.prescription_id, // UNCONFIRMED
      pharmacy_order_id: l.pharmacy_order_id || null, // UNCONFIRMED
      expected_qty: l.expected_qty,
      counted_qty: Number(l.counted_qty) // UNCONFIRMED
    };
    if (hasValue(String(l.reason || "").trim())) line.reason = String(l.reason).trim();
    return line;
  })
});

export const submitAudit = async (patientId, { imageId, lines }) => {
  const res = await axios.post(
    apiUrl(`/inventory/audit/submit/${patientId}`),
    buildAuditPayload({ imageId, lines }),
    { headers: authHeaders() }
  );
  return res.data?.message || "Audit submitted.";
};

/* ------------------------------------------------------------------ */
/* History — read-only                                                 */
/* ------------------------------------------------------------------ */

const normalizeHistoryLine = (r, index) => {
  const expected = toNumber(r.expected_qty);
  const counted = toNumber(pick(r, ["counted_qty", "actual_qty", "audited_qty", "qty"])); // UNCONFIRMED
  const noBalance = toBool(r.no_balance_row);
  return {
    key: `hl-${index}`,
    drug_name: pick(r, ["drug", "attestation_drug", "drug_name", "medication"], "—"), // UNCONFIRMED
    strength: pick(r, ["strength", "strength_value"], ""), // UNCONFIRMED
    schedule: scheduleTokenOf(pick(r, ["drug_schedule", "schedule"]), r.drug_subtype), // UNCONFIRMED
    expected_qty: expected,
    counted_qty: counted,
    no_balance_row: noBalance,
    variance:
      toNumber(r.variance) ??
      (!noBalance && expected !== null && counted !== null ? counted - expected : null),
    reason: pick(r, ["reason", "variance_reason"], "") // UNCONFIRMED
  };
};

export const getAuditHistory = async (patientId) => {
  const res = await axios.get(apiUrl(`/inventory/audit/history/${patientId}`), {
    headers: authHeaders()
  });
  return asArray(res.data?.data).map((a, index) => {
    const lines = asArray(pick(a, ["lines", "items", "details"], [])).map(normalizeHistoryLine); // UNCONFIRMED
    const varianceFromLines = lines.filter((l) => l.variance !== null && l.variance !== 0).length;
    return {
      key: `audit-${pick(a, ["audit_id", "id"], index)}`,
      id: pick(a, ["audit_id", "id"]), // UNCONFIRMED
      auditedAt: pick(a, ["audited_at", "submitted_at", "completed_at", "created_at"]), // UNCONFIRMED
      auditedBy: pick(a, ["audited_by_name", "submitted_by_name", "user_name", "audited_by", "created_by"]), // UNCONFIRMED
      // Presigned URL — may be null or expired; the screen falls back to a placeholder.
      imageUrl: pick(a, ["image_url", "presigned_url", "image_presigned_url"]), // UNCONFIRMED
      varianceCount: toNumber(pick(a, ["variance_count", "variances"]), varianceFromLines), // UNCONFIRMED
      lines
    };
  });
};

/* ------------------------------------------------------------------ */
/* Overdue report — the work queue                                     */
/* ------------------------------------------------------------------ */

export const getOverdueAudits = async ({ facilityId, groupId } = {}) => {
  const params = {};
  if (hasValue(facilityId)) params.facility_id = facilityId; // UNCONFIRMED
  if (hasValue(groupId)) params.group_id = groupId; // UNCONFIRMED

  const res = await axios.get(apiUrl("/inventory/audit/overdue"), {
    headers: authHeaders(),
    params
  });

  const rows = asArray(res.data?.data).map((r, index) => {
    const dueDate = pick(r, ["due_date", "next_due_date", "audit_due_date"]); // UNCONFIRMED
    const name =
      pick(r, ["patient_name"]) || // UNCONFIRMED
      [pick(r, ["last_name", "patient_last_name"]), pick(r, ["first_name", "patient_first_name"])]
        .filter(Boolean)
        .join(", ");
    return {
      key: `od-${pick(r, ["patient_id"], index)}-${index}`,
      patientId: pick(r, ["patient_id", "id"]), // UNCONFIRMED
      patientName: name || `Patient ${pick(r, ["patient_id", "id"], "")}`,
      facilityId: pick(r, ["facility_id"]), // UNCONFIRMED
      facilityName: pick(r, ["facility_name"], ""), // UNCONFIRMED
      dueDate,
      daysOverdue: toNumber(pick(r, ["days_overdue", "overdue_days"])), // UNCONFIRMED
      lastAuditAt: pick(r, ["last_audit_at", "last_audited_at", "last_audit_date"]), // UNCONFIRMED
      alwaysSkippable: toBool(r.always_skippable),
      state: hasValue(r.state) ? normalizeAuditState(r.state) : null
    };
  });

  // If the endpoint ignores the facility filter, apply it here.
  if (hasValue(facilityId)) {
    return rows.filter((r) => !hasValue(r.facilityId) || String(r.facilityId) === String(facilityId));
  }
  return rows;
};

/* ------------------------------------------------------------------ */
/* Group audit configuration (on the group config GET/PUT)             */
/* ------------------------------------------------------------------ */

// -999 = "never blocks / always skippable". 0 = blocks the moment it is due.
// Any other negative is rejected by the server. Never coerce this value.
export const GRACE_NEVER_BLOCKS = -999;

const AUDIT_FIELD_CANDIDATES = {
  enabled: ["inventory_audit_enabled", "audit_enabled", "inventory_audit_active"], // UNCONFIRMED
  intervalDays: ["inventory_audit_days", "audit_interval_days", "inventory_audit_interval"], // UNCONFIRMED
  // The hours part of "how often", saved as minutes on top of the days
  // (every 1 day 12 hrs → days 1, due_time 720).
  intervalMinutes: ["inventory_audit_due_time"], // pinned
  graceDays: ["inventory_audit_grace_days"], // pinned
  blocksMedpass: ["inventory_audit_blocks_medpass"] // pinned — no consumer yet
};

/**
 * Reads the audit fields from a group config response, remembering which
 * key each came from so the PUT writes back under the same name.
 */
export const readAuditConfig = (groupConfig) => {
  const g = groupConfig || {};
  const keys = {};
  Object.entries(AUDIT_FIELD_CANDIDATES).forEach(([field, candidates]) => {
    keys[field] = pickKey(g, candidates) || candidates[0];
  });

  const grace = toNumber(g[keys.graceDays], GRACE_NEVER_BLOCKS);
  // Stored minutes are shown as whole hours (rounded); full days roll into the
  // days, so the form always shows 0–23 hrs.
  const extraHours = Math.max(0, Math.round(toNumber(g[keys.intervalMinutes], 0) / 60));

  return {
    keys,
    present: Object.values(keys).some((k) => Object.prototype.hasOwnProperty.call(g, k)),
    hasMinutesField: Object.prototype.hasOwnProperty.call(g, keys.intervalMinutes),
    enabled: toBool(g[keys.enabled]),
    intervalDays: toNumber(g[keys.intervalDays], 0) + Math.floor(extraHours / 24),
    intervalHours: extraHours % 24,
    graceDays: grace,
    blocksMedpass: toBool(g[keys.blocksMedpass]),
    // Server-derived, read-only — never recomputed here (U16).
    canBlock: toBool(g.audit_can_block)
  };
};

/** The audit part of the group config PUT body, under the keys we read. */
export const auditConfigPayload = (cfg) => {
  const extraMinutes = (Number(cfg.intervalHours) || 0) * 60;
  return {
    [cfg.keys.enabled]: Boolean(cfg.enabled),
    [cfg.keys.intervalDays]: Number(cfg.intervalDays) || 0,
    // Sent only when the server already has the field or hours are set, so
    // saving keeps working on a server that hasn't added it yet.
    ...(cfg.hasMinutesField || extraMinutes > 0
      ? { [cfg.keys.intervalMinutes]: extraMinutes }
      : {}),
    [cfg.keys.graceDays]: Number(cfg.graceDays),
    [cfg.keys.blocksMedpass]: Boolean(cfg.blocksMedpass)
  };
};
